<template>
  <div class="contenedor-principal">
    <h1>Jornadas Europeas de Patrimonio: Cultura, Naturaleza y Tradición</h1>
    
    <h2>¿Qué son las Jornadas Europeas de Patrimonio?</h2>
    <p>Cada mes de octubre, Bizkaia se suma a las <strong>Jornadas Europeas de Patrimonio</strong>, una iniciativa promovida por el Consejo de Europa para acercar la riqueza cultural, histórica y natural a la ciudadanía. Es una oportunidad única para disfrutar de decenas de actividades gratuitas o de bajo coste repartidas por toda la comunidad.</p>
    
    <p>En Geobizi, consideramos que la conservación del patrimonio (tanto el geológico como el cultural) está intrínsecamente ligada a los Objetivos de Desarrollo Sostenible (ODS), por lo que somos participantes y organizadores habituales en estas jornadas.</p>
    
<p>Cada edición se vertebra en torno a una temática central. Por ejemplo, en 2022 el eje fue el <em>"Patrimonio Sostenible"</em>, y en 2023 se celebró el <em>"Patrimonio Vivo"</em>, centrado en el legado inmaterial. Cada año, al acercarse el mes de octubre, la Diputación Foral de Bizkaia publica la agenda oficial y las temáticas en sus canales institucionales para que la ciudadanía pueda consultar todas las actividades disponibles.</p>
    <h2>¿Qué se entiende por Patrimonio Cultural Inmaterial (PCI)?</h2>
    <p>La <strong>UNESCO</strong> define el Patrimonio Cultural Inmaterial (PCI) destacando su papel fundamental en prácticas, usos, expresiones, saberes y técnicas que son transmitidos de una generación a otra. Este legado intangible no solo otorga a las comunidades un arraigado sentido de identidad, sino que también promueve la creatividad y el bienestar social.</p>
    <p>Este rico legado se recrea continuamente, adaptándose a los cambios del entorno y a la interacción con la naturaleza. Este proceso dinámico confiere a las comunidades un profundo sentido de continuidad, contribuyendo a fomentar el respeto por la diversidad cultural.</p>

    <h3>Características del Patrimonio Cultural Inmaterial</h3>
    <ul>
      <li><strong>Comunitario:</strong> Naturaleza compartida por los miembros de una comunidad.</li>
      <li><strong>Transmisión oral:</strong> Se recrea y transmite principalmente de forma oral, a menudo desde la infancia.</li>
      <li><strong>Identitario:</strong> Integración absoluta en nuestra memoria colectiva.</li>
      <li><strong>Interconectado:</strong> Intrincada relación con la dimensión material de la cultura (lugares, herramientas).</li>
      <li><strong>Dinámico y Vital:</strong> No es un elemento fosilizado; está vivo, es ritualizado y cambia con el tiempo.</li>
      <li><strong>Sensorial:</strong> Proporciona una vivencia que se aprecia a través del olfato, el gusto y la percepción.</li>
      <li><strong>Vulnerable:</strong> Es susceptible de perderse si no se protege y se practica.</li>
    </ul>

    <h2>Ámbitos de manifestación del PCI</h2>
    <p>El PCI no se limita a compartimentos estancos; guarda una estrecha conexión con el patrimonio material (herramientas, instrumentos, indumentarias, lugares). Se manifiesta principalmente en:</p>

    <ul>
      <li><strong>Tradiciones y expresiones orales:</strong> La lengua como vehículo (ej. el dialecto <em>hika</em> en euskera), toponimia, refranes, cuentos y leyendas.</li>
      <li><strong>Artes del espectáculo:</strong> Música vocal e instrumental (romerías, pasacalles) y danzas tradicionales (<em>Dantzari-dantza</em>, <em>Ezpatadantza</em>).</li>
      <li><strong>Usos sociales, rituales y festivos:</strong> Eventos ligados al ciclo de la vida y festividades anuales (Carnaval, San Juan, Todos los Santos).</li>
      <li><strong>Técnicas artesanales tradicionales:</strong> Saberes y procesos empleados para crear herramientas, indumentaria, instrumentos musicales o enseres.</li>
      <li><strong>Conocimientos relacionados con la Naturaleza:</strong> Creencias sobre la fauna, flora, meteorología, ciclos lunares y la medicina tradicional (uso de plantas medicinales).</li>
    </ul>

    <h2>Nuestro granito de arena: Geobizi en las Jornadas</h2>
    <p>Nuestra participación en las Jornadas Europeas de Patrimonio es una constante que nos permite innovar y acercar la ciencia a la calle:</p>
    <ul>
      <li><strong>Edición 2022:</strong> Nos estrenamos bajo el lema de patrimonio sostenible. Destacaron nuestras rutas de geoturismo por el <em>Flysch de Getxo</em> y la actividad <em>"Rocas que hablan"</em> en Algorta.</li>
      <li><strong>Edición 2023:</strong> Participamos con una actividad inmersiva sobre los <em>Eguzkilores</em>, combinando historia, mitología vasca y manualidades.</li>
      <li><strong>Edición 2024:</strong> Llevamos la geología al siguiente nivel realizando la ruta del Flysch de Getxo, pero añadiendo un componente tecnológico: ver las rocas y microfósiles a través de un microscopio electrónico de campo.</li>
    </ul>

    <div class="caja-recurso">
      <h3>Organiza una actividad patrimonial con nosotros</h3>
      <p>
        ¿Te has quedado sin plaza en las Jornadas Oficiales? ¿Quieres que adaptemos uno de nuestros talleres (como el del Eguzkilore o la ruta del Flysch) para tu colegio, asociación, empresa o grupo familiar? 
      </p>
      <div class="botones-recurso">
        <router-link to="/calendario" class="proximas-actividades">Ver catálogo de actividades</router-link>
        <router-link to="/contacto" class="btn-ver-mas">Contactar con nosotros</router-link>
      </div>
    </div>

    <p class="social-media"><b>¡Síguenos en redes sociales para estar al tanto de cuándo abrimos inscripciones para las próximas jornadas!</b></p>
    <div class="redes-sociales">
      <a href="https://www.instagram.com/geotxiki/" target="_blank" rel="noopener noreferrer" title="Geobizi en Instagram">
        <img src="/imagenes/iconos/instagram.png" alt="Geobizi en Instagram" class="social-icon" loading="lazy" />
      </a>
      <a href="https://www.facebook.com/geobizirik/" target="_blank" rel="noopener noreferrer" title="Geobizi en Facebook">
        <img src="/imagenes/iconos/facebook.png" alt="Geobizi en Facebook" class="social-icon" loading="lazy" />
      </a>
      <a href="https://www.tiktok.com/@geobizi" target="_blank" rel="noopener noreferrer" title="Geobizi en TikTok">
        <img src="/imagenes/iconos/tiktok.png" alt="Geobizi en TikTok" class="social-icon" loading="lazy" />
      </a>
    </div>

    <div class="imagen-trio">
      <div>
        <img
          class="img"
          src="/imagenes/blog/detalle/patrimonio1.avif"
          alt="Participantes en un taller divulgativo durante las Jornadas de Patrimonio Europeo"
          title="Taller divulgativo — Patrimonio"
          loading="lazy"
        />
      </div>
      <div>
        <img
          class="img"
          src="/imagenes/blog/detalle/patrimonio2.avif"
          alt="Ruta guiada por el Flysch de Getxo explicando el patrimonio geológico local"
          title="Ruta geológica por el Flysch"
          loading="lazy"
        />
      </div>
      <div>
        <img
          class="img"
          src="/imagenes/blog/detalle/patrimonio3.avif"
          alt="Taller de manualidades y tradiciones vascas centrado en el Eguzkilore"
          title="Taller tradicional del Eguzkilore"
          loading="lazy"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

// URL optimizada para ser "Evergreen" (Recuerda actualizar tu router/index.js)
const pageUrl = 'https://www.geobizi.com/blog/patrimonio-europeo'
const ogImage = 'https://www.geobizi.com/imagenes/blog/detalle/patrimonio1.avif'

useHead({
  // Título SEO atemporal
  title: 'Jornadas Europeas de Patrimonio en Bizkaia | Geobizi',
  meta: [
    { name: 'description', content: 'Descubre qué es el Patrimonio Cultural Inmaterial (PCI) y conoce la participación y actividades de Geobizi en las Jornadas Europeas de Patrimonio de Bizkaia.' },
    { name: 'robots', content: 'index, follow' },
    { name: 'author', content: 'Geobizi' },
    { name: 'theme-color', content: '#0b8a4c' },
    { name: 'language', content: 'es' },
    { property: 'og:title', content: 'Jornadas Europeas de Patrimonio: Cultura y Naturaleza | Geobizi' },
    { property: 'og:description', content: 'Definición de patrimonio inmaterial y resumen de nuestras actividades históricas en las jornadas de Bizkaia.' },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: pageUrl },
    { property: 'og:image', content: ogImage },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: ogImage }
  ],
  link: [
    { rel: 'canonical', href: pageUrl },
    { rel: 'image_src', href: ogImage }
  ],
  script: [
  {
    type: 'application/ld+json',
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "headline": "Jornadas Europeas de Patrimonio: Cultura, Naturaleza y Tradición",
          "description": "Definición del Patrimonio Cultural Inmaterial de la UNESCO y la trayectoria de actividades de Geobizi en las Jornadas Europeas de Patrimonio.",
          "image": ogImage,
          "author": {
            "@type": "Organization",
            "name": "Geobizi",
            "url": "https://www.geobizi.com"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Geobizi",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.geobizi.com/imagenes/GeobiziLogo.png"
            }
          },
          "datePublished": "2023-09-20T08:00:00+01:00",
          "dateModified": "2026-04-14T10:00:00+02:00",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": pageUrl
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": "https://www.geobizi.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Blog",
              "item": "https://www.geobizi.com/blog"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Jornadas de Patrimonio",
              "item": pageUrl
            }
          ]
        }
      ]
    })
  }
]
})
</script>

<style scoped>
/* CAJA DE RECURSOS */
.caja-recurso {
  background-color: var(--megashoftgreen); 
  border-left: 4px solid var(--shoftgreen); 
  padding: 1.5rem;
  margin: 2.5rem 0;
  border-radius: 0 0.5rem 0.5rem 0;
}
.caja-recurso h3 {
  margin-top: 0;
  color: var(--lightgreen);
  font-size: 1.2rem;
}
.caja-recurso p {
  margin-bottom: 1.5rem;
  color: var(--darkgrey);
}
.botones-recurso {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.proximas-actividades {
  background-color: var(--supershoftgreen);
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border-radius: 4px;
  transition: background-color 0.3s;
}
.proximas-actividades:hover {
  background-color: var(--shoftgreen);
  color: var(--white);
}
.btn-ver-mas {
  background-color: transparent;
  color: var(--lightgreen);
  padding: 0.6rem 1.2rem;
  text-decoration: none;
  font-weight: bold;
  border: 1px solid var(--shoftgreen);
  border-radius: 4px;
  transition: all 0.3s;
}
.btn-ver-mas:hover {
  background-color: var(--shoftgreen);
  color: white;
}

/* REDES SOCIALES */
.social-media {
  margin-top: 2rem;
  color: var(--darkgrey);
}
.redes-sociales {
  margin-left: 1rem;
  display: flex;
  align-items: center;
}
.social-icon {
  width: 2.5rem; 
  margin-right: 1rem;
  margin-bottom: 1.5rem;
  transition: transform 0.3s;
}
.social-icon:hover {
  transform: scale(1.1);
}

/* LISTAS CON HOJITAS */
.contenedor-principal ul {
  list-style-type: none;
  padding-left: 0;
}
.contenedor-principal li {
  position: relative;
  padding-left: 2rem;
  margin-bottom: 0.8rem;
  line-height: 1.5;
}
.contenedor-principal li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  width: 1.2rem;
  height: 1.2rem;
  background-image: url('../../assets/Hojitas.png'); 
  background-size: contain;
  background-repeat: no-repeat;
}
</style>